import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Grid,
  Stack,
  styled,
  IconButton,
  Switch,
  Paper,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ICalendarAccount,
  ICandidate,
  IOutlookCalendarAccount,
  createCalendarEvent,
  createOutlookCalendarEvent,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  updateGoogleCalendarEvent,
  updateOutlookCalendarEvent,
  IOutlookCalendarEvent,
  ICalendarEvent,
} from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  getCurrentZoneAbbr,
  getStandardTime,
  WsDateTimePicker,
  ControlledTextArea,
  TagsInput,
  WsSelect,
  WsButton,
  WsTypography,
  WsIconText,
  WsTypoColors,
  WsDialog,
  GoogleMeetIcon,
  MicrosoftTeamsIcon,
  WsTypoVariants,
  ChatBoxHeader,
} from 'wisestep-base';
import { object, string, array } from 'yup';

import useCopyToClipboard from '../../features/candidates/talentpool/hooks/useCopyToClipboard';
import { success, failed } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

import { formDefaultValue, formDefaultValueWhileEdit } from './formDefaultValue';

const FormTagsInput = styled(TagsInput)(() => ({
  '.MuiOutlinedInput-root': {
    padding: '4.5px 8px',
    margin: 0,
  },
}));

interface ICalendarEventProps {
  account: ICalendarAccount | null;
  oAccount: IOutlookCalendarAccount | null;
  candidate?: ICandidate | null;
  defaultValues?: IOutlookCalendarEvent;
  updateForOutlook?: boolean;
  onClose: () => void;
  onDelete?: (eventInfo?: { eventId: string; selectedMeeting: string }) => Promise<void>;
}

export const VideoConferenceOptions = {
  GOOGLE_MEET: 'google',
  TEAMS: 'teams',
};

const CalendarEvent: FC<ICalendarEventProps> = ({
  account,
  oAccount,
  candidate,
  onClose,
  updateForOutlook,
  defaultValues,
  onDelete,
}) => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [googleCalender, setGoogleCalender] = useState(false);
  const [openGoogleFailure, setOpenGoogleFailure] = useState(false);
  const [googleAccount, setGoogleAccount] = useState<ICalendarAccount | null>(account);
  const [outlookCalendarAccount, setOutlookCalendarAccount] =
    useState<IOutlookCalendarAccount | null>(oAccount);
  const [microsoftCalender, setMicrosoftCalender] = useState(false);
  const [openMicrosoftFailure, setOpenMicrosoftFailure] = useState(false);
  const calendarMeetingsObj:
    | Record<string, { label: string; value: string; icon: JSX.Element }>
    | Record<string, never> = useMemo(() => {
    const obj = {};
    if (googleCalender || defaultValues?.entityType === VideoConferenceOptions.GOOGLE_MEET)
      obj[VideoConferenceOptions.GOOGLE_MEET] = {
        label: 'Google Meet',
        value: VideoConferenceOptions.GOOGLE_MEET,
        icon: <GoogleMeetIcon />,
      };
    if (microsoftCalender || defaultValues?.entityType === VideoConferenceOptions.TEAMS)
      obj[VideoConferenceOptions.TEAMS] = {
        label: 'Teams',
        value: VideoConferenceOptions.TEAMS,
        icon: <MicrosoftTeamsIcon />,
      };
    return obj;
  }, [defaultValues?.entityType, googleCalender, microsoftCalender]);
  const [createdMeetingEvent, setCreatedMeetingEvent] = useState<
    ICalendarEvent | IOutlookCalendarEvent
  >();
  const [isMeetingCreated, setIsMeetingCreated] = useState<boolean>(false);
  const [, copy] = useCopyToClipboard();

  const setForm = () => {
    return candidate
      ? {
          resolver: yupResolver(
            object().shape({
              guest: array().of(string().email()).required('Guests email is required'),
            })
          ),
          defaultValues: formDefaultValue(candidate),
        }
      : {
          resolver: yupResolver(
            object().shape({
              guest: array().of(string().email()).required('Guests email is required'),
            })
          ),
          defaultValues: defaultValues
            ? formDefaultValueWhileEdit(defaultValues)
            : formDefaultValue(null),
        };
  };

  const generateEventWithConference = async (
    payload: ICalendarEvent | IOutlookCalendarEvent,
    conferenceOption
  ) => {
    const googleConference = conferenceOption === 'google';

    try {
      const response = googleConference
        ? await createCalendarEvent({
            ...(payload as ICalendarEvent),
            meeting: conferenceOption,
            googleOAuth: googleAccount ?? null,
          })
        : await createOutlookCalendarEvent({
            ...payload,
            meeting: conferenceOption,
            outlookOAuthToken: outlookCalendarAccount ?? null,
          });
      const createdEvent: ICalendarEvent = response.data;
      setNotification(success('Conference link generated!'));
      return createdEvent;
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
  };

  const onSubmit = async (data: FieldValues, conferenceOption?: string) => {
    setDisableSubmit(true);
    let reminder = data.reminder;
    if (data.reminderIn == 'Hour' || data.reminderIn == 'hours') {
      reminder = data.reminder * 60;
    }
    const forMeeting = !isEmpty(conferenceOption);
    const payload = {
      attendees: data.guest,
      entityId: candidate?.id ?? '',
      entityType: data.entityType,
      eventDescription: data.description,
      eventSummary: data.title,
      eventId: data.eventId,
      from: data.from,
      to: data.to,
      location: data.location,
      reminders: [reminder],
      googleOAuth: null,
    };
    if (forMeeting) return generateEventWithConference(payload, conferenceOption);
    try {
      if (googleCalender) {
        if (defaultValues && !updateForOutlook) {
          await updateGoogleCalendarEvent({ ...payload, googleOAuth: googleAccount ?? null });
        } else
          await createCalendarEvent({
            ...payload,
            ...(isMeetingCreated &&
              selectedMeeting !== VideoConferenceOptions.GOOGLE_MEET && { eventId: undefined }),
            googleOAuth: googleAccount ?? null,
          });
      }
      if (microsoftCalender) {
        if (defaultValues && updateForOutlook)
          await updateOutlookCalendarEvent({
            ...payload,
            outlookOAuthToken: outlookCalendarAccount ?? null,
          });
        else
          await createOutlookCalendarEvent({
            ...payload,
            outlookOAuthToken: outlookCalendarAccount ?? null,
            ...(isMeetingCreated &&
              selectedMeeting === VideoConferenceOptions.GOOGLE_MEET && { eventId: undefined }),
          });
      }
      if (defaultValues) setNotification(success('Event updated in calendar'));
      else setNotification(success('Event created in calendar'));
      onClose();
    } catch (e) {
      setDisableSubmit(false);
      setNotification(failed('Some error occurred. Please try later'));
    }
  };

  const sendRemainder = (event: React.ChangeEvent<HTMLInputElement>) =>
    setValue('sendEmail', event.target.checked);

  const checkGoogleCalender = async () => {
    if (!googleCalender) {
      const req = await getCalendarAccounts();
      if (req?.data && req.data.length > 0) {
        setGoogleAccount(req.data[0]);
      } else {
        setOpenGoogleFailure(!googleCalender);
        setGoogleAccount(null);
      }
    }
    setGoogleCalender(!googleCalender);
  };

  const checkMicrosoftCalender = async () => {
    if (!microsoftCalender) {
      const req = await getOutlookCalendarAccounts();
      if (req?.data && req.data.length > 0) {
        setOutlookCalendarAccount(req.data[0]);
      } else {
        setOpenMicrosoftFailure(!microsoftCalender);
        setOutlookCalendarAccount(null);
      }
    }
    setMicrosoftCalender(!microsoftCalender);
  };

  const redirectToGoogleCalenderSettings = () => {
    window.location.href = '/calender-settings';
  };

  const redirectToMicrosoftCalenderSettings = () => {
    window.location.href = '/calender-settings/microsoft';
  };

  const closeGoogleFailure = () => {
    setOpenGoogleFailure(false);
    setGoogleCalender(false);
  };

  const closeMicrosoftFailure = () => {
    setOpenMicrosoftFailure(false);
    setMicrosoftCalender(false);
  };

  const setDefault = () => {
    setGoogleCalender(googleAccount != null && googleAccount.email != '');
    setMicrosoftCalender(outlookCalendarAccount != null && outlookCalendarAccount.email != '');
  };

  useEffect(setDefault, [googleAccount, outlookCalendarAccount]);
  useEffect(() => {
    if (!isEmpty(defaultValues) && defaultValues?.entityType) {
      setSelectedMeeting(defaultValues.entityType);
      setCreatedMeetingEvent(defaultValues);
      setIsMeetingCreated(true);
    }
  }, [defaultValues]);

  const methods = useForm(setForm());

  const { handleSubmit, setValue, reset, watch } = methods;
  const [selectedMeeting, setSelectedMeeting] = useState<string>();

  const createDummyEventAndFetchMeetLink = async (conferenceOption: string) => {
    setIsMeetingCreated(false);
    handleSubmit(async (data) => {
      const createEventAndFetch: ICalendarEvent | undefined = await onSubmit(
        data,
        conferenceOption
      );
      const optionString =
        VideoConferenceOptions.GOOGLE_MEET === conferenceOption ? 'Google' : 'Teams';
      const contentString =
        `Join ${optionString} Video conference` +
        `\nConference Link: ${createEventAndFetch?.meeting}\n`;
      const description =
        data.description ??
        (createEventAndFetch?.eventDescription
          ? contentString + createEventAndFetch?.eventDescription
          : contentString);
      setCreatedMeetingEvent(createEventAndFetch);
      reset({ ...formDefaultValueWhileEdit(createEventAndFetch), description });
      setIsMeetingCreated(true);
      setDisableSubmit(false);
    })();
  };
  const onChangeOfConferenceOption = (event: SelectChangeEvent<string>) => {
    if (isMeetingCreated && onDelete) {
      onDelete({
        eventId: createdMeetingEvent?.eventId as string,
        selectedMeeting: selectedMeeting as string,
      });
      reset({ ...watch(), eventId: undefined, description: undefined });
    }
    setSelectedMeeting(event.target.value);
    createDummyEventAndFetchMeetLink(event.target.value);
  };

  return (
    <FormProvider {...methods}>
      {account || oAccount ? (
        <form onSubmit={handleSubmit((data) => onSubmit(data))}>
          <ChatBoxHeader>
            <Grid container item xs={12}>
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold={true}>
                Setup a Calendar Event
              </WsTypography>
            </Grid>
          </ChatBoxHeader>
          <Item>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <WsTypography>Title</WsTypography>
              </Grid>
              <Grid item xs={8}>
                <ControlledTextInput
                  name="title"
                  labelText=""
                  fullWidth
                  id="event_title"
                  placeholder="Add a title"
                  variant="outlined"
                  autoComplete="event_title"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <WsTypography>{`From (${getCurrentZoneAbbr()} ${getStandardTime()})`}</WsTypography>
              </Grid>
              <Grid item xs={8}>
                <WsDateTimePicker name="from" label="Select date/time" size="small" />
              </Grid>
              <Grid item xs={4}>
                <WsTypography>{`To (${getCurrentZoneAbbr()} ${getStandardTime()})`}</WsTypography>
              </Grid>
              <Grid item xs={8}>
                <WsDateTimePicker name="to" label="Select date/time" size="small" />
              </Grid>
              <Grid item xs={4}>
                <WsTypography>Location</WsTypography>
              </Grid>
              <Grid item xs={8}>
                <ControlledTextInput
                  name="location"
                  labelText=""
                  fullWidth
                  id="event_title"
                  placeholder="Add location"
                  variant="outlined"
                  autoComplete="event_title"
                />
              </Grid>
              <Grid item xs={4}>
                <WsTypography>Add Guests</WsTypography>
              </Grid>
              <Grid item xs={8}>
                <FormTagsInput
                  name="guest"
                  placeholder="Enter guests email"
                  multiple={true}
                  initialValue={[]}
                />
              </Grid>
              <Grid item xs={4}>
                <WsTypography>Remind me in</WsTypography>
              </Grid>
              <Grid item xs={4}>
                <ControlledTextInput
                  name="reminder"
                  labelText=""
                  type="number"
                  fullWidth
                  id="event_title"
                  placeholder="Add reminder"
                  variant="outlined"
                  autoComplete="event_title"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <WsSelect
                  name="reminderIn"
                  label=""
                  selectArray={[
                    { label: 'Hour', value: 'Hour' },
                    { label: 'Minutes', value: 'Minutes' },
                  ]}
                  defaultValue="Minutes"
                  placeholder=""
                />
              </Grid>
              <Grid item container xs={12} columnSpacing={1}>
                <Grid item xs={7}>
                  <Select
                    value={selectedMeeting}
                    defaultValue={
                      defaultValues && defaultValues.entityType
                        ? defaultValues.entityType
                        : undefined
                    }
                    onChange={onChangeOfConferenceOption}
                    displayEmpty
                    fullWidth
                    style={{ height: '2.5rem', fontWeight: 'bold' }}
                    renderValue={(value) =>
                      isEmpty(value) ? (
                        <WsTypography color={WsTypoColors.PRIMARY600} bold cursor="pointer">
                          Setup a google or teams meeting
                        </WsTypography>
                      ) : (
                        <Grid container columnSpacing={2} alignItems="center">
                          <Grid item xs={2} pt="0.3rem">
                            {calendarMeetingsObj[value ?? ''].icon}
                          </Grid>
                          <Grid item xs={8} overflow="hidden">
                            <WsTypography variant={WsTypoVariants.BODY2}>
                              {isMeetingCreated
                                ? createdMeetingEvent?.meeting ?? `Join ${value} meeting`
                                : calendarMeetingsObj[value ?? ''].label}
                            </WsTypography>
                          </Grid>
                          <Grid item container xs={2}>
                            {!isMeetingCreated && <CircularProgress size="1rem" color="primary" />}
                          </Grid>
                        </Grid>
                      )
                    }
                  >
                    {Object.values(calendarMeetingsObj).map((meeting) => (
                      <MenuItem key={meeting.value} value={meeting.value}>
                        <ListItemIcon>{meeting.icon}</ListItemIcon>
                        <ListItemText>
                          <WsTypography cursor="pointer">{meeting.label}</WsTypography>
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={2}>
                  {isMeetingCreated && (
                    <IconButton onClick={() => copy(createdMeetingEvent?.meeting ?? '')}>
                      <ContentCopyIcon color="primary" fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <WsTypography>Description</WsTypography>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextArea name="description" />
              </Grid>
              <Grid item xs={9} py={1}>
                <WsIconText
                  icon={<Switch onChange={sendRemainder} defaultChecked />}
                  text={'Notify by email'}
                />
              </Grid>
              <Grid item container xs={5} py={1}>
                <WsIconText
                  icon={<Switch onChange={checkGoogleCalender} checked={googleCalender} />}
                  text={'Add to Google Calender'}
                />
              </Grid>
              <Grid item xs={7} py={1}>
                {googleAccount && googleCalender ? (
                  <WsTypography color={WsTypoColors.PRIMARY}>{googleAccount.email}</WsTypography>
                ) : googleAccount ? (
                  <></>
                ) : (
                  <WsButton
                    size="medium"
                    variant="text"
                    onClick={() => setOpenMicrosoftFailure(true)}
                  >
                    Configure Now
                  </WsButton>
                )}
              </Grid>
              <Grid item container xs={5} py={1}>
                <WsIconText
                  icon={<Switch onChange={checkMicrosoftCalender} checked={microsoftCalender} />}
                  text={'Add to Microsoft Calender'}
                />
              </Grid>
              <Grid item xs={7} py={1}>
                {outlookCalendarAccount && microsoftCalender ? (
                  <WsTypography color={WsTypoColors.PRIMARY}>
                    {outlookCalendarAccount.email}
                  </WsTypography>
                ) : outlookCalendarAccount ? (
                  <></>
                ) : (
                  <WsButton size="medium" variant="text" onClick={() => setOpenGoogleFailure(true)}>
                    Configure Now
                  </WsButton>
                )}
              </Grid>
              <Grid item xs={12} mt={2}>
                <Box sx={{ float: 'right' }}>
                  <Stack direction="row" spacing={1.5}>
                    <WsButton
                      variant="text"
                      onClick={() => {
                        if (createdMeetingEvent && !defaultValues) {
                          onDelete &&
                            onDelete({
                              eventId: createdMeetingEvent.eventId as string,
                              selectedMeeting: selectedMeeting as string,
                            });
                          onClose();
                        } else onClose();
                      }}
                    >
                      Cancel
                    </WsButton>
                    <WsButton variant="contained" type="submit" disabled={disableSubmit}>
                      Save
                    </WsButton>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Item>
        </form>
      ) : (
        <Grid container>
          <Grid container item xs={12} justifySelf="flex-end" justifyContent="flex-end">
            <IconButton onClick={onClose as () => void}>
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid mt={1} mb={2} container justifyContent="center">
            <Stack direction="column" spacing={2}>
              <WsTypography>
                No account is configured, Please configure your account to set up a meeting
              </WsTypography>
              <Grid item xs={12} display="flex" justifyContent="end" py={2}>
                <Box>
                  <WsButton onClick={() => navigate('/calender-settings')}>
                    Configure gmail
                  </WsButton>
                </Box>
                <Box pl={0.5}>
                  <WsButton onClick={() => navigate('/calender-settings/microsoft')}>
                    Configure outlook
                  </WsButton>
                </Box>
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      )}

      <WsDialog openDialog={openGoogleFailure}>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
          <Box py={1}>
            <WsTypography>Please configure your email to set up a Google meeting.</WsTypography>
          </Box>
          <Box py={1} pl={0.5}>
            <WsButton size="medium" variant="contained" onClick={redirectToGoogleCalenderSettings}>
              Configure Now
            </WsButton>
            <WsButton size="medium" variant="text" onClick={closeGoogleFailure}>
              Skip
            </WsButton>
          </Box>
        </Paper>
      </WsDialog>
      <WsDialog openDialog={openMicrosoftFailure}>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
          <Box py={1} pl={0.5}>
            <WsTypography>Please configure your email to set up a Microsoft meeting.</WsTypography>
          </Box>
          <Box py={1} pl={0.5}>
            <WsButton
              size="medium"
              variant="contained"
              onClick={redirectToMicrosoftCalenderSettings}
            >
              Configure Now
            </WsButton>
            <WsButton size="medium" variant="text" onClick={closeMicrosoftFailure}>
              Skip
            </WsButton>
          </Box>
        </Paper>
      </WsDialog>
    </FormProvider>
  );
};

export default CalendarEvent;
